import { apiClient } from "../../lib/http/apiClient";
import type { AuthSession, AuthUser, LoginResponse, SignupInitResponse } from "./types";

const toSession = (payload: LoginResponse): AuthSession => ({
  accessToken: payload.access_token,
  user: payload.user,
});

export const authService = {
  async login(identifier: string, password: string): Promise<AuthSession> {
    const normalizedIdentifier = identifier.includes("@")
      ? identifier.trim().toLowerCase()
      : identifier.trim();
    const { data } = await apiClient.post<LoginResponse>("/api/v1/auth/login", {
      identifier: normalizedIdentifier,
      password,
    });
    return toSession(data);
  },

  // Phase 1 of OTP signup: sends a phone OTP, account not created yet.
  // Email is optional and unverified at this stage. Password is optional too —
  // when given it's hashed server-side and set on the account once the OTP is
  // verified, so it can be used as a login fallback alongside phone/OTP.
  async signup(name: string, phone: string, role: string, email?: string, password?: string): Promise<SignupInitResponse> {
    const normalizedRole = role === "dealer" ? "seller" : role === "user" ? "buyer" : role;
    const { data } = await apiClient.post<SignupInitResponse>("/api/v1/auth/signup", {
      name: name.trim(),
      phone: phone.trim(),
      role: normalizedRole,
      email: email?.trim().toLowerCase() || undefined,
      password: password || undefined,
    });
    return data;
  },

  // Phase 2 of OTP signup: verifies the phone OTP and creates the account.
  async verifySignupOtp(phone: string, phoneOtp: string): Promise<AuthSession> {
    const { data } = await apiClient.post<LoginResponse>("/api/v1/auth/verify-signup-otp", {
      phone,
      phone_otp: phoneOtp,
    });
    return toSession(data);
  },

  async resendSignupOtp(phone: string): Promise<{ expires_in_seconds: number }> {
    const { data } = await apiClient.post<{ expires_in_seconds: number }>("/api/v1/auth/resend-otp", {
      channel: "whatsapp",
      target: phone,
      purpose: "signup",
    });
    return data;
  },

  async logout(deviceId?: string): Promise<void> {
    await apiClient.post("/api/v1/auth/logout", { device_id: deviceId ?? null });
  },

  async getCurrentUser(): Promise<AuthUser> {
    const { data } = await apiClient.get<AuthUser>("/api/v1/profile");
    return data;
  },

  async googleSignin(idToken: string, role?: string): Promise<AuthSession> {
    const { data } = await apiClient.post<LoginResponse>("/api/v1/auth/google", {
      id_token: idToken,
      role: role ?? null,
    });
    return toSession(data);
  },

  async appleSignin(
    identityToken: string,
    opts?: { role?: string; userName?: string },
  ): Promise<AuthSession> {
    const { data } = await apiClient.post<LoginResponse>("/api/v1/auth/apple", {
      identity_token: identityToken,
      role: opts?.role ?? null,
      user_name: opts?.userName ?? null,
    });
    return toSession(data);
  },

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post("/api/v1/auth/forgot-password", { email: email.trim().toLowerCase() });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post("/api/v1/auth/reset-password", { token, new_password: newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post("/api/v1/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
};
